import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import { Package, X } from 'lucide-react';
import { downloadStudyBuild, readErrorMessage } from '../../services/studyPromotionApi';

const ExportCandidateModal = ({ isOpen, onClose, studyId, studyCode, studyTitle }) => {
  const [label, setLabel] = useState('');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error('Enter a candidate label, e.g. "DR1-rc1".');
      return;
    }
    setExporting(true);
    try {
      await downloadStudyBuild(studyId, trimmed, `${studyCode || studyId}-${trimmed}.fxbuild.json`);
      toast.success(`Candidate "${trimmed}" exported.`);
      setLabel('');
      onClose();
    } catch (err) {
      toast.error(await readErrorMessage(err, 'Export failed.'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => !exporting && onClose()} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/40 p-6">
          <div className="flex items-start justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <Package className="w-5 h-5 mr-2 text-primary-600" />
              Export Candidate Build
            </Dialog.Title>
            <button
              onClick={() => !exporting && onClose()}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Downloads the complete design-time build of{' '}
            <span className="font-medium">{studyTitle}</span> as a checksummed candidate
            package (.fxbuild.json), ready to deploy into another environment. No
            participant, response, or user data is included.
          </p>

          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Candidate label
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleExport()}
            placeholder='e.g. "DR1-rc1"'
            maxLength={255}
            autoFocus
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-1"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
            Names this candidate through review and deployment, e.g. a release-candidate tag.
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => !exporting && onClose()}
              disabled={exporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !label.trim()}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Package className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export Package'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ExportCandidateModal;
